import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';

export const useActionLoading = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingElement, setLoadingElement] = useState<HTMLElement | null>(null);
  const [loadingPosition, setLoadingPosition] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!isLoading || !loadingElement) {
      setLoadingPosition(null);
      return;
    }

    // Calculate position relative to the element
    const updatePosition = () => {
      if (!loadingElement) return;
      const rect = loadingElement.getBoundingClientRect();
      // Position loader to the right of the element, vertically centered
      setLoadingPosition({
        x: rect.right + 8,
        y: rect.top + rect.height / 2,
      });
    };

    // Initial position
    updatePosition();

    // Update position on scroll or resize
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isLoading, loadingElement]);

  const executeWithLoading = useCallback(async (
    action: (e?: React.MouseEvent) => void | Promise<void>,
    event?: React.MouseEvent
  ) => {
    // Get the element that was clicked
    const clickedElement = event?.currentTarget as HTMLElement;
    if (clickedElement) {
      setLoadingElement(clickedElement);
    }
    setIsLoading(true);

    try {
      const result = action(event) as any;
      if (result && typeof result.then === 'function') {
        await result;
      }
    } catch (error) {
      console.error('Error executing action:', error);
      throw error;
    } finally {
      setIsLoading(false);
      setLoadingElement(null);
      setLoadingPosition(null);
    }
  }, []);

  const LoadingIndicator = () => {
    if (!isLoading || !loadingPosition || typeof document === 'undefined') {
      return null;
    }

    return createPortal(
      <div
        className="fixed pointer-events-none z-[3000]"
        style={{
          left: loadingPosition.x,
          top: loadingPosition.y,
          transform: 'translateY(-50%)', // Center vertically
        }}
      >
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-1.5">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
        </div>
      </div>,
      document.body
    );
  };

  return {
    isLoading,
    executeWithLoading,
    LoadingIndicator,
  };
};

